import React, { useMemo } from "react"
import PropTypes from "prop-types"
import ReactApexChart from "react-apexcharts"

import { COLOR_ANTERIOR, MODO_IMPORTE } from "../static"
import { corto, moneda, numero, piezas as fmtPiezas } from "../format"
import { OPCIONES_MODO } from "../useTrendCard"
import SegmentedToggle from "./SegmentedToggle"
import PeriodField from "./PeriodField"

/**
 * Opciones de ApexCharts alineadas con el diseño: sin barra de herramientas ni
 * la sombra de la plantilla Skote, retícula tenue, año anterior en gris y
 * degradado bajo la serie del periodo actual.
 */
const opcionesGrafica = (labels, acento, modo) => ({
  chart: {
    toolbar: { show: false },
    zoom: { enabled: false },
    fontFamily: "inherit",
    animations: { easing: "easeout", speed: 260 },
  },
  colors: [COLOR_ANTERIOR, acento],
  dataLabels: { enabled: false },
  stroke: { curve: "smooth", width: [2, 2.4] },
  fill: {
    type: ["solid", "gradient"],
    gradient: { opacityFrom: 0.24, opacityTo: 0, shadeIntensity: 1 },
  },
  markers: { size: 0, hover: { size: 5 } },
  grid: {
    borderColor: "#f0f2f5",
    strokeDashArray: 0,
    xaxis: { lines: { show: false } },
    padding: { left: 4, right: 8 },
  },
  legend: { show: false },
  tooltip: {
    theme: "dark",
    shared: true,
    y: { formatter: valor => (modo === MODO_IMPORTE ? moneda(valor) : numero(valor)) },
  },
  xaxis: {
    categories: labels,
    tickAmount: Math.min(labels.length, 12),
    axisBorder: { show: false },
    axisTicks: { show: false },
    labels: { style: { colors: "#b6bcc7", fontSize: "10px" } },
  },
  yaxis: {
    labels: {
      formatter: valor => corto(valor, modo),
      style: { colors: "#b6bcc7", fontSize: "10px" },
    },
  },
})

const TrendChartCard = ({
  titulo,
  acento,
  state,
  events,
  proveedores,
  etiquetaProveedores,
}) => {
  const { modo, labels, series, resumen } = state
  const esImporte = modo === MODO_IMPORTE

  const opciones = useMemo(
    () => opcionesGrafica(labels, acento, modo),
    [labels, acento, modo]
  )

  // En «Piezas y Kgs» se dibujan los kilos: las piezas viajan en la columna de
  // cifras, porque dos pares de series en la misma escala son ilegibles.
  const fuente = esImporte ? series.importe : series.kgs

  const datos = useMemo(
    () =>
      fuente
        ? [
            { name: `${titulo} año anterior`, data: fuente.previous || [] },
            { name: `${titulo} periodo actual`, data: fuente.current || [] },
          ]
        : [],
    [fuente, titulo]
  )

  const formatoPrincipal = valor => (esImporte ? moneda(valor) : `${numero(valor)} Kgs`)

  return (
    <div className="res-panel res-anim">
      <div className="res-panel-head">
        <h2 className="res-panel-title">
          <span className="res-dot" style={{ background: acento }} />
          {titulo}
        </h2>

        <div className="res-panel-tools">
          <SegmentedToggle
            opciones={OPCIONES_MODO}
            valor={modo}
            onChange={events.onChangeModo}
          />

          {proveedores && (
            <select
              className="res-select"
              value={state.proveedor}
              onChange={evento => events.onChangeProveedor(evento.target.value)}
            >
              <option value="">{etiquetaProveedores}</option>
              {proveedores.map(proveedor => (
                <option key={proveedor.id} value={proveedor.id}>
                  {proveedor.name}
                </option>
              ))}
            </select>
          )}

          <PeriodField
            periodo={state.periodo}
            onChange={events.onChangePeriodo}
          />
        </div>
      </div>

      <div className="res-panel-body">
        <div className="res-stats">
          <div>
            <div className="res-stat-label">Periodo actual</div>
            <div className="res-stat-value">
              {resumen ? formatoPrincipal(resumen.actual) : "—"}
            </div>
            {!esImporte && resumen && (
              <div className="res-stat-value is-prev">
                {fmtPiezas(resumen.piezasActual)}
              </div>
            )}

            {resumen && resumen.variacion !== null && (
              <div className="res-chip-row">
                <span
                  className={`res-chip${resumen.variacion < 0 ? " is-down" : ""}`}
                >
                  {resumen.variacion < 0 ? "▼" : "▲"}
                  {Math.abs(resumen.variacion).toFixed(2)}%
                </span>
                <span className="res-chip-note">vs año anterior</span>
              </div>
            )}
          </div>

          <div className="res-rule" />

          <div>
            <div className="res-stat-label">Año anterior</div>
            <div className="res-stat-value is-prev">
              {resumen ? formatoPrincipal(resumen.anterior) : "—"}
            </div>
            {!esImporte && resumen && (
              <div className="res-stat-value is-prev">
                {fmtPiezas(resumen.piezasAnterior)}
              </div>
            )}
          </div>

          <div className="res-stat-box">
            <div className="res-stat-box-label">Promedio diario</div>
            <div className="res-stat-box-value">
              {resumen ? formatoPrincipal(resumen.promedio) : "—"}
            </div>
            <div className="res-stat-box-label">Día más alto</div>
            <div className="res-stat-box-value">
              {resumen ? formatoPrincipal(resumen.pico) : "—"}{" "}
              {resumen && resumen.diaPico > 0 && (
                <small>día {resumen.diaPico}</small>
              )}
            </div>
          </div>
        </div>

        <div className="res-chart">
          {fuente ? (
            <>
              <ReactApexChart
                series={datos}
                options={opciones}
                type="area"
                height={240}
              />
              <div className="res-legend">
                <div className="res-legend-item">
                  <span
                    className="res-legend-key"
                    style={{ background: COLOR_ANTERIOR }}
                  />
                  {titulo} año anterior
                </div>
                <div className="res-legend-item">
                  <span
                    className="res-legend-key"
                    style={{ background: acento }}
                  />
                  {titulo} periodo actual
                </div>
              </div>
            </>
          ) : (
            <div className="res-empty">
              {state.isLoading
                ? "Cargando la serie del periodo…"
                : "No hay movimientos en el periodo seleccionado."}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

TrendChartCard.propTypes = {
  titulo: PropTypes.string.isRequired,
  acento: PropTypes.string.isRequired,
  state: PropTypes.object.isRequired,
  events: PropTypes.object.isRequired,
  proveedores: PropTypes.array,
  etiquetaProveedores: PropTypes.string,
}

TrendChartCard.defaultProps = {
  etiquetaProveedores: "--- Todos ---",
}

export default TrendChartCard
